/**
 * ProtectedRoute
 *
 * Guards all app routes. Performs two checks:
 *  1. Authentication — redirects unauthenticated users to /login
 *  2. Subscription   — redirects strata_admin / property_manager users
 *                      without an active subscription to /pricing
 *
 * Hosts and system_admin bypass the subscription check.
 * Routes /pricing and /account/billing are explicitly exempt so users can
 * always reach those pages to start or manage their subscription.
 */
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useSubscription, PAID_ROLES } from "@/hooks/useSubscription";
import type { UserRole } from "@/types/database";

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles?: UserRole[];
}

/** Routes where the subscription guard is skipped (must always be accessible). */
const SUBSCRIPTION_EXEMPT_PATHS = ["/pricing", "/account/billing"];

export function ProtectedRoute({ children, allowedRoles }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading: authLoading, user, effectiveRole } = useAuth();
  const { isActive, requiresSubscription, isLoading: subLoading, error: subError } = useSubscription();
  const location = useLocation();

  // ── Loading state ────────────────────────────────────────────────────────
  // Do NOT block on sub loading if the subscription query has already errored —
  // useSubscription's queryFn returns null on error rather than throwing, but
  // this guard handles any unexpected error path so the spinner never hangs.
  const isLoading =
    authLoading ||
    (isAuthenticated && subLoading && requiresSubscription && !subError);

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          <p className="text-sm text-muted-foreground">Loading…</p>
        </div>
      </div>
    );
  }

  // ── 1. Auth check ────────────────────────────────────────────────────────
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // ── 2. Role check ────────────────────────────────────────────────────────
  // system_admin bypasses role checks (they can access all routes when impersonating)
  if (allowedRoles && user && user.role !== "system_admin" && !allowedRoles.includes(effectiveRole)) {
    return <Navigate to="/dashboard" replace />;
  }

  // ── 3. Subscription check ────────────────────────────────────────────────
  // Skip if on an exempt route (pricing / billing themselves)
  // system_admin is never gated by subscription regardless of effectiveRole
  const isExemptPath = SUBSCRIPTION_EXEMPT_PATHS.some((p) =>
    location.pathname.startsWith(p)
  );

  if (requiresSubscription && !isActive && !isExemptPath && user?.role !== "system_admin") {
    // Preserve current path so we can redirect back after subscribing
    return <Navigate to="/pricing" state={{ from: location }} replace />;
  }

  return <>{children}</>;
}
